import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import MissingContributions from './MissingContributions';
import ContributionForm from './ContributionForm';
import { Calculator, History, Download, Mail, AlertCircle, Settings, Users, FileText, FileOutput } from 'lucide-react';
import YearDataManagement from './YearDataManagement';
import AdminHistory from './AdminHistory';
import JPKGeneration from './JPKGeneration';
import BulkSupporterContributions from './BulkSupporterContributions';


// Create a TabGroup component for admin sections
const TabGroup = ({ children, activeTab, setActiveTab }) => {
    const tabs = [
        { id: 'yearData', label: 'Year Data Management', icon: Settings },
        { id: 'missing', label: 'Missing Contributions', icon: Users },
        { id: 'history', label: 'Contribution History', icon: FileText },
        { id: 'jpk', label: 'JPK Generation', icon: FileOutput },
        { id: 'bulkSupporterContributions', label: 'Bulk Supporter Contributions', icon: FileOutput },
    ];

    return (
        <div className="space-y-4">
            <div className="border-b border-gray-200">
                <nav className="flex space-x-8" aria-label="Tabs">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                  py-4 px-1 inline-flex items-center gap-2
                  border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }
                `}
                            >
                                <Icon className="h-5 w-5" />
                                {__(tab.label)}
                            </button>
                        );
                    })}
                </nav>
            </div>
            <div className="mt-4">
                {children}
            </div>
        </div>
    );
};

const AdminView = ({ history, handleRegenerateEmail, handleGenerateOldPdf }) => {
    const [activeTab, setActiveTab] = useState('yearData');

    return (
        <TabGroup activeTab={activeTab} setActiveTab={setActiveTab}>
            {activeTab === 'yearData' && (
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <YearDataManagement />
                </div>
            )}

            {activeTab === 'missing' && (
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            {__('Missing Contributions Overview')}
                        </h3>
                    </div>
                    <div className="p-6">
                        <MissingContributions />
                    </div>
                </div>
            )}

            {activeTab === 'history' && (
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                            <History className="h-5 w-5 text-gray-500" />
                            {__('Contribution History')}
                        </h3>
                    </div>
                    <div className="p-6">
                        <>
                            <AdminHistory />
                        </>
                        {/* ) : (
                            <p className="text-gray-500 text-center py-4">
                                {__('No history available.')}
                            </p>
                        )} */}
                    </div>
                </div>
            )}
            {activeTab === 'jpk' && <JPKGeneration />}
            {activeTab === 'bulkSupporterContributions' && <BulkSupporterContributions />}
        </TabGroup>
    );
};
interface YearData {
    id: number;
    title: { rendered: string };
    meta: {
        finance_ranges: Array<{ from: string; to: string; amount: string }>;
        finance_supporter_contribution: string;
    };
}

const AnnualData: React.FC = () => {
    const [years, setYears] = useState<YearData[]>([]);
    const [income, setIncome] = useState<string>('');
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const [contribution, setContribution] = useState<string | null>(null);
    const [calculatedContribution, setCalculatedContribution] = useState(null);
    const [isAccepted, setIsAccepted] = useState<boolean>(false);
    const [pdfLink, setPdfLink] = useState<string | null>(null);
    const [history, setHistory] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const memberType = boardMemberData?.memberType || 'regular';
    const isAdmin = boardMemberData?.isAdmin || false;

    const restURL = boardMemberData?.restURL || '';
    const nonce = boardMemberData?.nonce || '';
console.log('boardMemberData', boardMemberData)
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch year data with error handling
                const yearResponse = await fetch(`${restURL}wp/v2/year_data`, {
                    headers: {
                        'X-WP-Nonce': nonce,
                        'Content-Type': 'application/json',
                    },
                });

                if (!yearResponse.ok) {
                    throw new Error(`Failed to fetch year data: ${yearResponse.statusText}`);
                }

                const yearData = await yearResponse.json();
                if (!Array.isArray(yearData)) {
                    throw new Error('Invalid year data format received');
                }
                console.log("yd", yearData);
                // Sort years in descending order
                const sortedYears = yearData
                    .filter(year => year && year.title && year.title.rendered)
                    .sort((a, b) =>
                        parseInt(b.title.rendered) - parseInt(a.title.rendered)
                    );
                setYears(sortedYears);

                // Fetch history data with error handling
                const historyResponse = await fetch(`${restURL}board-member/v1/history`, {
                    headers: {
                        'X-WP-Nonce': nonce,
                        'Content-Type': 'application/json',
                    },
                });
                console.log("hr", historyResponse);
                if (!historyResponse.ok) {
                    throw new Error(`Failed to fetch history: ${historyResponse.statusText}`);
                }

                const historyData = await historyResponse.json();
                if (!Array.isArray(historyData)) {
                    throw new Error('Invalid history data format received');
                }

                const sortedHistory = historyData
                    .sort((a, b) => b.year.localeCompare(a.year));
                setHistory(sortedHistory);

            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error instanceof Error ? error.message : 'An unknown error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [restURL, nonce]);

    const availableYears = React.useMemo(() => {
        return years.filter((year) => {
            return !history.some((entry) => entry.year === year.title.rendered);
        });
    }, [years, history]);

    const calculateContribution = () => {
        if (!selectedYear) return;

        const yearData = years.find(year => year.id === parseInt(selectedYear));
        if (!yearData || !yearData.meta) {
            setError('Invalid year data');
            return;
        }

        try {
            if (memberType === 'regular' && income) {
                const incomeValue = parseFloat(income);
                const range = yearData.meta.finance_ranges?.find(r => {
                    const from = parseFloat(r.from);
                    const to = parseFloat(r.to);
                    return incomeValue >= from && incomeValue <= to;
                });

                setContribution(range ? range.amount : __('No matching range found'));
            } else if (memberType === 'supporter') {
                setContribution(yearData.meta.finance_supporter_contribution || '0');
            }
        } catch (error) {
            console.error('Error calculating contribution:', error);
            setError('Error calculating contribution');
        }

        setIsAccepted(false);
    };

    const handleYearChange = React.useCallback((e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedYear(e.target.value);
        setCalculatedContribution(null);
    }, []);

    const handleIncomeChange = React.useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setIncome(e.target.value);
        setCalculatedContribution(null);
    }, []);

    const handleIncomeSubmit = React.useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        calculateContribution();
    }, [calculateContribution]);

    const handleAcceptContribution = async () => {
        if (!selectedYear || !contribution) return;

        try {
            const response = await fetch(`${restURL}board-member/v1/accept-contribution`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': nonce,
                },
                body: JSON.stringify({
                    year_id: selectedYear,
                    income: income,
                    contribution: contribution,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.code === 'duplicate_entry') {
                setError(__('You have already submitted data for this year.'));
            } else if (data.success && data.pdf_link) {
                alert(__('Contribution accepted, PDF generated, and email sent.'));
                setPdfLink(data.pdf_link);
                setHistory(data.history);
                setIsAccepted(true);
                if (data.payment_deadline) {
                    alert(`${__('Payment Deadline')}: ${data.payment_deadline}`);
                }
            } else {
                throw new Error(__('Failed to accept contribution.'));
            }
        } catch (error) {
            console.error('Error accepting contribution:', error);
            setError(error instanceof Error ? error.message : __('An error occurred while accepting the contribution.'));
        }
    };

    const handleRegenerateEmail = async (historyId: number) => {
        try {
            await fetch(`${boardMemberData.restURL}board-member/v1/regenerate-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce,
                },
                body: JSON.stringify({ history_id: historyId }),
            });
            alert(__('Email has been regenerated and sent.'));
        } catch (error) {
            console.error('Error regenerating email:', error);
        }
    };

    const handleGenerateOldPdf = async (historyId: number) => {
        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/generate-old-pdf`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce,
                },
                body: JSON.stringify({ history_id: historyId }),
            });
            const data = await response.json();

            if (data.pdf_link) {
                alert(__('PDF has been regenerated.'));
                window.open(data.pdf_link, '_blank');
            } else {
                alert(__('Failed to generate PDF.'));
            }
        } catch (error) {
            console.error(__('Error generating PDF:'), error);
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }


    return (
        <div className="space-y-8">
            {/* Error Alert */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* <h1>{__('Annual Data Management')}</h1> */}


            {/* Admin Section - Missing Contributions */}
            {isAdmin ? (
                <AdminView
                    history={history}
                    handleRegenerateEmail={handleRegenerateEmail}
                    handleGenerateOldPdf={handleGenerateOldPdf}
                />
            ) : (
                <>
                    <div className="flex space-x-2">
                        <div className="bg-white shadow rounded-lg overflow-hidden w-3/5">
                            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                    <Calculator className="h-5 w-5 text-gray-500" />
                                    {__('Calculate Contribution')}
                                </h3>
                            </div>
                            <div className="p-6">
                                <ContributionForm
                                    selectedYear={selectedYear}
                                    income={income}
                                    memberType={memberType}
                                    availableYears={availableYears}
                                    onYearChange={handleYearChange}
                                    onIncomeChange={handleIncomeChange}
                                    onSubmit={handleIncomeSubmit}
                                />

                                {contribution && selectedYear && (
                                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="text-lg font-medium text-gray-900">
                                            {__('Calculated Contribution')}
                                        </h4>
                                        <p className="text-2xl font-semibold text-blue-600 mt-2">
                                            {contribution} PLN
                                        </p>
                                        {!isAccepted && (
                                            <button
                                                onClick={handleAcceptContribution}
                                                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                            >
                                                {__('Accept Contribution')}
                                            </button>
                                        )}
                                        {isAccepted && pdfLink && (
                                            <div className="mt-4">
                                                <p className="text-green-600 mb-2">
                                                    {__('Contribution Accepted.')}
                                                </p>
                                                <a
                                                    href={pdfLink}
                                                    download
                                                    className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
                                                >
                                                    <Download className="h-4 w-4 mr-2" />
                                                    {__('Download PDF')}
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            </div>
                            <div className='bg-white rounded-lg overflow-hidden w-2/5'>
                                <p className='mb-0 p-6 whitespace-pre-line italic leading-6' >
                                    {__('In accordance with the Chamber Statute approved by resolution no. 16/2022 of June 27, 2022 of the Regulations on the amount of membership fees, dependent on the member status, are established by the Chamber Council at the request of the President of the Chamber.')} <br />
                                    {__('According to the statute, Chamber Members are required to notify the Chamber of the amount of revenues from water supply and sewage operations (revenues from water supply operations, including wholesale, sewerage operations, including wholesale, from operations related to rainwater and meltwater, and delivered sewage) by 30 March 2025.')}
                                </p>
                            </div>
                    </div>


                    {/* History Section */}
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                <History className="h-5 w-5 text-gray-500" />
                                {__('Contribution History')}
                            </h3>
                        </div>
                        <div className="p-6">
                            {history.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {__('Year')}
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {__('Income')}
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {__('Contribution')}
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {__('Date')}
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {__('Actions')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {history.map((entry) => (
                                                <tr key={entry.id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {entry.year}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {memberType === 'regular' ? `${entry.income} MLN PLN` : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {entry.contribution} PLN
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {entry.date}
                                                        {entry?.accepted_by && (
                                                            <span className="block text-xs text-gray-500">
                                                                {__('Accepted by')}: {entry?.accepted_by.user_name} <br />
                                                                {__('IP Address')}: {entry?.accepted_by.ip_address}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <div className="flex space-x-2">
                                                            {/* {isAdmin && ( */}
                                                                <button
                                                                    onClick={() => handleRegenerateEmail(entry.id)}
                                                                    className="text-blue-600 hover:text-blue-900"
                                                                >
                                                                    <Mail className="h-5 w-5" />
                                                                </button>
                                                            {/* )} */}
                                                            <button
                                                                onClick={() => handleGenerateOldPdf(entry.id)}
                                                                className="text-blue-600 hover:text-blue-900"
                                                            >
                                                                <Download className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="text-gray-500 text-center py-4">
                                    {__('No history available.')}
                                </p>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AnnualData;